import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import { GRADES } from '@/browse/grades'
import { isSubjectDomainLocked, isSubjectGradeLocked, isSubjectLocked } from '@/contentprofile/premium_locks'
import type { ContentProfileSubject } from '@/contentprofile/premium_locks'
import type { FilterDropdownFilter, FilterDropdownOption } from '@/components/filter_dropdown'
import { betaOptIn } from '@/util/beta_opt_in'

export const INSTRUCTIONAL_LEVELS = ['less', 'some', 'more']
export const DEFAULT_INSTRUCTIONAL_LEVEL = INSTRUCTIONAL_LEVELS[0]

const LEVEL_OF_SUPPORT_BY_INSTRUCTIONAL_LEVEL: Record<string, string> = { less: '0', some: '1', more: '2' }

export interface GoalWizardSelection {
  subject: string
  category: string
  grade: string
  instructionalLevel: string
}

export type GoalWizardField = keyof GoalWizardSelection

export const EMPTY_SELECTION: GoalWizardSelection = { subject: '', category: '', grade: '', instructionalLevel: '' }

function firstQueryValue(value: LocationQuery[string] | undefined): string {
  const first = Array.isArray(value) ? value[0] : value
  return first ?? ''
}

export function selectionFromQuery(query: LocationQuery): GoalWizardSelection {
  return {
    subject: firstQueryValue(query.subject),
    category: firstQueryValue(query.category),
    grade: firstQueryValue(query.grade),
    instructionalLevel: firstQueryValue(query.instructionalLevel)
  }
}

// Starting over and closing the wizard clear the selection but keep the beta opt-in, so a
// reload stays on this page rather than dropping the reviewer onto its Vue 2 version.
export function selectionToQuery(selection: GoalWizardSelection, currentQuery: LocationQuery): LocationQueryRaw {
  return {
    ...betaOptIn(currentQuery),
    subject: selection.subject || undefined,
    category: selection.category || undefined,
    grade: selection.grade || undefined,
    instructionalLevel: selection.instructionalLevel || undefined
  }
}

export function withAnswer(selection: GoalWizardSelection, field: GoalWizardField, value: string): GoalWizardSelection {
  const answered = { ...selection, [field]: value }
  if (field === 'subject') {
    answered.category = ''
  }
  if (field === 'grade' && !selection.instructionalLevel) {
    answered.instructionalLevel = DEFAULT_INSTRUCTIONAL_LEVEL
  }
  return answered
}

function offeredUnlocked(value: string, offered: string[], isLocked: boolean): string {
  return offered.includes(value) && !isLocked ? value : ''
}

function answeredInstructionalLevel(level: string): string {
  if (!level) {
    return DEFAULT_INSTRUCTIONAL_LEVEL
  }
  return INSTRUCTIONAL_LEVELS.includes(level) ? level : ''
}

// A value the wizard does not offer, or one that is locked, reads as unanswered, so neither
// an outdated link nor a hand-edited one can reach past the prompts or into premium content.
export function answeredSelection(subjects: ContentProfileSubject[], selection: GoalWizardSelection): GoalWizardSelection {
  const offeredSubjects = subjects.map((entry) => entry.subject)
  const subject = offeredUnlocked(selection.subject, offeredSubjects, isSubjectLocked(subjects, selection.subject))
  const subjectProfile = subjects.find((entry) => entry.subject === subject)
  return {
    subject,
    category: offeredUnlocked(
      selection.category,
      subjectProfile?.categories ?? [],
      isSubjectDomainLocked(subjects, subject, selection.category)
    ),
    grade: offeredUnlocked(selection.grade, subjectProfile?.grades ?? [], isSubjectGradeLocked(subjects, subject, selection.grade)),
    instructionalLevel: answeredInstructionalLevel(selection.instructionalLevel)
  }
}

function lockableOptions(values: string[], isLocked: (value: string) => boolean): FilterDropdownOption[] {
  return values.map((value) => ({ label: value, value, locked: isLocked(value) }))
}

function prompt(field: GoalWizardField, valueLabel: string, emptyText: string, options: FilterDropdownOption[]): FilterDropdownFilter {
  return { field, valueLabel, emptyText, options, disabled: false }
}

export interface GoalWizardPrompts {
  subject: FilterDropdownFilter
  category: FilterDropdownFilter
  grade: FilterDropdownFilter
  instructionalLevel: FilterDropdownFilter
}

export function buildPrompts(subjects: ContentProfileSubject[], selection: GoalWizardSelection): GoalWizardPrompts {
  const subjectProfile = subjects.find((entry) => entry.subject === selection.subject)
  return {
    subject: prompt(
      'subject',
      selection.subject,
      'subject',
      lockableOptions(subjects.map((entry) => entry.subject), (subject) => isSubjectLocked(subjects, subject))
    ),
    category: prompt(
      'category',
      selection.category,
      'domain (optional)',
      lockableOptions(subjectProfile?.categories ?? [], (category) => isSubjectDomainLocked(subjects, selection.subject, category))
    ),
    grade: prompt(
      'grade',
      selection.grade,
      'grade',
      lockableOptions(subjectProfile?.grades ?? [], (grade) => isSubjectGradeLocked(subjects, selection.subject, grade))
    ),
    instructionalLevel: prompt(
      'instructionalLevel',
      selection.instructionalLevel,
      DEFAULT_INSTRUCTIONAL_LEVEL,
      INSTRUCTIONAL_LEVELS.map((level) => ({ label: level, value: level }))
    )
  }
}

function gradeOrder(grade: string): number {
  return GRADES.indexOf(grade)
}

// The student's own grade when it has goals, otherwise the nearest lower grade that does.
// An empty list means no grade at or below theirs has a goal to show.
export function redirectGrades(enrolledGrade: string, gradesWithResults: string[]): string[] {
  if (gradesWithResults.includes(enrolledGrade)) {
    return [enrolledGrade]
  }
  const lowerGradesWithResults = gradesWithResults
    .filter((grade) => gradeOrder(grade) >= 0 && gradeOrder(grade) < gradeOrder(enrolledGrade))
    .sort((gradeA, gradeB) => gradeOrder(gradeA) - gradeOrder(gradeB))
  return lowerGradesWithResults.slice(-1)
}

export function browseGoalsQuery(selection: GoalWizardSelection, grades: string[], currentQuery: LocationQuery): LocationQueryRaw {
  return {
    ...betaOptIn(currentQuery),
    subject: selection.subject,
    subjectDomain: selection.category || undefined,
    grades,
    levelOfSupport: LEVEL_OF_SUPPORT_BY_INSTRUCTIONAL_LEVEL[selection.instructionalLevel]
  }
}
